import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Sparkles, Loader2, ArrowRight, RefreshCw, Send, Bot, User as UserIcon, Eye, X, AlertTriangle, Settings } from 'lucide-react';
import { aiAPI, logsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useModalKeyboard } from '../hooks/useModalKeyboard';
import { Tooltip, Button, Card } from './ui';

const ejemplosPrompt = [
    'Cotización para landing corporativa con tienda online y backend personalizado',
    'Branding completo + identidad visual para nueva marca de cosméticos',
    'Campaña de redes sociales por 3 meses',
    'App móvil con backend y panel admin'
];

const ASISTENTE_NOMBRE = 'Don Pre';

const mensajeInicial = {
    role: 'model',
    content: `👋 Hola, soy ${ASISTENTE_NOMBRE}, tu asistente de cotizaciones. Cuéntame qué necesita el cliente y te ayudo a armar la propuesta. Puedo preguntar detalles si necesito más info.`
};

const GenerarCotizacionIA = () => {
    const navigate = useNavigate();
    const { tienePermiso } = useAuth();
    const puedeConfigurar = tienePermiso('configuracion');
    const [messages, setMessages] = useState([mensajeInicial]);
    const [input, setInput] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [propuestaActual, setPropuestaActual] = useState(null);
    const [propuestaVista, setPropuestaVista] = useState(null); // Para modal detalle
    const [lastUserMessage, setLastUserMessage] = useState(null);

    // Estado del sistema IA (circuit breaker). null = cargando, {...} = cargado
    const [aiStatus, setAiStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);

    useModalKeyboard({ open: !!propuestaVista, onClose: () => setPropuestaVista(null) });
    const chatEndRef = useRef(null);
    const inputRef = useRef(null);

    // Al montar, consultar estado del sistema IA
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await aiAPI.getStatus();
                if (alive) setAiStatus(res.data);
            } catch {
                if (alive) setAiStatus({ any_available: true, ia_activada: true, providers: {} });
            } finally {
                if (alive) setStatusLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Auto-scroll al final
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, enviando]);

    // Enfocar input al cargar
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Auto-resize del textarea segun el contenido (tope 140px)
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 140) + 'px';
    }, [input]);

    const enviar = async (texto) => {
        const mensajeTexto = texto || input.trim();
        if (!mensajeTexto || enviando) return;

        setLastUserMessage(mensajeTexto);

        // Agregar mensaje del usuario
        const userMsg = { role: 'user', content: mensajeTexto };
        const newMessages = [...messages, userMsg];
        setMessages(newMessages);
        setInput('');
        setEnviando(true);

        try {
            // Mandar al backend solo los mensajes (sin el inicial del bot ni errores previos)
            const messagesParaApi = newMessages.filter(m => m !== mensajeInicial && !m.error);
            const res = await aiAPI.chatCotizacion(messagesParaApi);
            const { mensaje, propuesta, _proveedor, _modelo, _fallback_usado } = res.data;

            const botMsg = {
                role: 'model',
                content: mensaje || 'Listo',
                propuesta: propuesta || null,
                proveedor: _proveedor,
                modelo: _modelo,
                fallback_usado: _fallback_usado
            };
            setMessages(prev => [...prev, botMsg]);

            if (propuesta) {
                setPropuestaActual(propuesta);
            }
        } catch (err) {
            toast.error('Error: ' + err.message);
            setMessages(prev => [...prev, { role: 'model', content: '❌ Error al procesar. Intenta de nuevo.', error: true }]);
            // Reportar a system_logs para no perder el rastro del error de IA.
            logsAPI.reportFrontend({
                level: 'error',
                category: 'ia:chat',
                mensaje: err?.message || 'Error IA chat',
                stack: err?.stack || null,
                contexto: {
                    status: err?.status || null,
                    lastUserMessage: mensajeTexto?.substring(0, 500) || null,
                    turnos: newMessages.filter(m => !m.error).length
                },
                url: typeof window !== 'undefined' ? window.location.href : null
            });
        } finally {
            setEnviando(false);
            inputRef.current?.focus();
        }
    };

    const reintentar = () => {
        if (!lastUserMessage || enviando) return;
        // Quita el mensaje de error y el último mensaje del usuario antes de reintentar
        setMessages(prev => {
            let n = prev.length;
            if (prev[n - 1]?.error) n--;
            if (prev[n - 1]?.role === 'user') n--;
            return prev.slice(0, n);
        });
        setTimeout(() => enviar(lastUserMessage), 50);
    };

    const aplicar = () => {
        if (!propuestaActual) {
            toast.error('Aún no hay propuesta para aplicar');
            return;
        }
        // Sanea la propuesta antes de navegar al form. Objetivo: prevenir el
        // "pantalla en blanco" cuando la IA devuelve un formato inesperado —
        // clientes/servicios null, arrays con items rotos, ausencia de nombres.
        const propuestaLimpia = {
            ...propuestaActual,
            cliente: propuestaActual.cliente && typeof propuestaActual.cliente === 'object'
                ? propuestaActual.cliente
                : null,
            servicios: Array.isArray(propuestaActual.servicios)
                ? propuestaActual.servicios.filter(s => s && typeof s === 'object').map((s, idx) => ({
                    ...s,
                    tipo_servicio_id: s.tipo_servicio_id || null,
                    tipo_servicio_nombre: s.tipo_servicio_nombre || '',
                    // El titulo es obligatorio en el form — proponemos algo razonable si la IA no lo dio.
                    titulo: (s.titulo && String(s.titulo).trim())
                        || s.tipo_servicio_nombre
                        || `Servicio ${idx + 1}`,
                    asignaciones: Array.isArray(s.asignaciones)
                        ? s.asignaciones.filter(a => a && typeof a === 'object')
                        : [],
                    costos_fijos: Array.isArray(s.costos_fijos)
                        ? s.costos_fijos.filter(c => c && typeof c === 'object')
                        : []
                }))
                : [],
            extras: Array.isArray(propuestaActual.extras)
                ? propuestaActual.extras.filter(e => e && typeof e === 'object')
                : []
        };

        if (propuestaLimpia.servicios.length === 0) {
            toast.error('La propuesta no incluye servicios válidos. Ajusta con otra pregunta.');
            return;
        }

        navigate('/cotizaciones/nueva', { state: { aiData: propuestaLimpia } });
    };

    const reiniciarConversacion = () => {
        setMessages([mensajeInicial]);
        setPropuestaActual(null);
        setLastUserMessage(null);
        setInput('');
        inputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            enviar();
        }
    };

    // Bloqueo pantalla completa cuando IA no esta disponible.
    // Se muestra ANTES del chat para que el usuario no intente enviar.
    if (!statusLoading && aiStatus && !aiStatus.any_available) {
        const razon = !aiStatus.ia_activada
            ? {
                titulo: 'Sistema IA desactivado',
                mensaje: 'El sistema de IA está apagado globalmente desde configuración.'
            }
            : (() => {
                const providers = aiStatus.providers || {};
                const errores = Object.entries(providers)
                    .filter(([_, p]) => !p.available && p.message)
                    .map(([name, p]) => {
                        const label = name === 'gemini' ? 'Gemini'
                            : name === 'deepseek' ? 'DeepSeek'
                            : name === 'claude' ? 'Claude'
                            : name;
                        return `${label}: ${p.message}`;
                    });
                return {
                    titulo: 'IA no disponible',
                    mensaje: errores.length
                        ? errores.join(' · ')
                        : 'Los proveedores de IA no están respondiendo. Revisa la configuración.'
                };
            })();

        return (
            <Card className="ai-unavailable-card">
                <Card.Body>
                    <div className="ai-unavailable-content">
                        <div className="ai-unavailable-icon">
                            <AlertTriangle size={48} />
                        </div>
                        <h2 className="ai-unavailable-title">{razon.titulo}</h2>
                        <p className="ai-unavailable-message">{razon.mensaje}</p>
                        <p className="ai-unavailable-hint">
                            {puedeConfigurar
                                ? 'Verifica los API keys, saldo o el switch global de IA en Configuración.'
                                : 'Contacta a un administrador para revisar la configuración de IA.'}
                        </p>
                        {puedeConfigurar && (
                            <Button
                                variant="primary"
                                leftIcon={<Settings size={16} />}
                                onClick={() => navigate('/configuracion-ia')}
                            >
                                Ver ajustes de IA
                            </Button>
                        )}
                    </div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <div className="card ai-chat-card">
            <div className="card-header ai-chat-header">
                <h2 className="ai-title">
                    <Sparkles size={22} className="ai-icon" />
                    Generar Cotización con IA
                </h2>
                <div className="ai-chat-actions">
                    {propuestaActual && (
                        <button className="btn neon" type="button" onClick={aplicar}>
                            Crear cotización <ArrowRight size={14} />
                        </button>
                    )}
                    <button className="btn ghost" type="button" onClick={reiniciarConversacion} disabled={enviando}>
                        <RefreshCw size={14} /> Nueva
                    </button>
                </div>
            </div>

            <div className="ai-chat-body">
                <div className="ai-chat-messages">
                    {messages.map((msg, i) => {
                        const isLastError = msg.error && i === messages.length - 1;
                        return (
                        <div key={i} className={`ai-msg ai-msg-${msg.role} ${msg.error ? 'ai-msg-error' : ''}`}>
                            <div className="ai-msg-avatar" title={msg.role === 'model' ? ASISTENTE_NOMBRE : ''}>
                                {msg.role === 'user' ? <UserIcon size={14} /> : <Bot size={14} />}
                            </div>
                            <div className="ai-msg-content">
                                {msg.role === 'model' && (
                                    <div className="ai-msg-sender">
                                        {ASISTENTE_NOMBRE}
                                        {msg.proveedor && (
                                            <span className={`ai-msg-proveedor ai-msg-proveedor-${msg.proveedor}`}>
                                                {msg.proveedor === 'gemini' ? 'Gemini' : 'DeepSeek'}
                                                {msg.fallback_usado && ' · fallback'}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div className="ai-msg-text">{msg.content}</div>
                                {isLastError && lastUserMessage && (
                                    <button
                                        type="button"
                                        className="ai-msg-retry"
                                        onClick={reintentar}
                                        disabled={enviando}
                                    >
                                        <RefreshCw size={12} /> Reintentar
                                    </button>
                                )}
                                {msg.propuesta && (
                                    <div className="ai-msg-propuesta">
                                        <div className="ai-propuesta-header">
                                            <Sparkles size={12} /> Propuesta generada
                                        </div>

                                        {msg.propuesta.razonamiento && (
                                            <div className="ai-propuesta-razon">
                                                {msg.propuesta.razonamiento}
                                            </div>
                                        )}

                                        {msg.propuesta.cliente && (msg.propuesta.cliente.nombre || msg.propuesta.cliente.empresa) && (
                                            <div className="ai-propuesta-section">
                                                <div className="ai-propuesta-label">Cliente</div>
                                                <div className="ai-propuesta-text">
                                                    {msg.propuesta.cliente.nombre}
                                                    {msg.propuesta.cliente.empresa && ` · ${msg.propuesta.cliente.empresa}`}
                                                    {msg.propuesta.cliente.es_nuevo && ' (nuevo)'}
                                                </div>
                                            </div>
                                        )}

                                        {msg.propuesta.servicios?.length > 0 && (
                                            <div className="ai-propuesta-section">
                                                <div className="ai-propuesta-label">Servicios ({msg.propuesta.servicios.length})</div>
                                                {msg.propuesta.servicios.map((s, j) => (
                                                    <div key={j} className="ai-propuesta-servicio">
                                                        <strong>{s.tipo_servicio_nombre}</strong>
                                                        {s.asignaciones?.length > 0 && (
                                                            <span className="ai-propuesta-meta">
                                                                {' · '}{s.asignaciones.reduce((a, b) => a + (b.horas || 0), 0)}h
                                                            </span>
                                                        )}
                                                        {s.costos_fijos?.length > 0 && (
                                                            <span className="ai-propuesta-meta">
                                                                {' · '}{s.costos_fijos.length} costo(s) fijo(s)
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {msg.propuesta.extras?.length > 0 && (
                                            <div className="ai-propuesta-section">
                                                <div className="ai-propuesta-label">Extras ({msg.propuesta.extras.length})</div>
                                            </div>
                                        )}

                                        <button
                                            type="button"
                                            className="ai-propuesta-ver-detalle"
                                            onClick={() => setPropuestaVista(msg.propuesta)}
                                        >
                                            <Eye size={12} /> Ver detalle completo
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        );
                    })}

                    {enviando && (
                        <div className="ai-msg ai-msg-model">
                            <div className="ai-msg-avatar"><Bot size={14} /></div>
                            <div className="ai-msg-content">
                                <div className="ai-msg-text ai-typing">
                                    <span></span><span></span><span></span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div ref={chatEndRef} />
                </div>

                {messages.length <= 1 && (
                    <div className="ai-examples">
                        <span className="ai-examples-label">Ejemplos para empezar:</span>
                        <div className="ai-examples-list">
                            {ejemplosPrompt.map((ej, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    className="ai-example-chip"
                                    onClick={() => enviar(ej)}
                                    disabled={enviando}
                                >
                                    {ej}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="ai-chat-input-wrap">
                    <textarea
                        ref={inputRef}
                        className="ai-chat-input"
                        rows={1}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Escribe un mensaje... (Enter para enviar, Shift+Enter nueva línea)"
                        disabled={enviando}
                    />
                    <Tooltip content="Enviar mensaje">
                        <button
                            className="ai-chat-send"
                            type="button"
                            onClick={() => enviar()}
                            disabled={enviando || input.trim().length === 0}
                            aria-label="Enviar"
                        >
                            {enviando ? <Loader2 size={18} className="spin" /> : <Send size={18} />}
                        </button>
                    </Tooltip>
                </div>
            </div>

            {/* Modal: Ver detalle completo de propuesta */}
            {propuestaVista && (
                <div className="modal-overlay" onClick={() => setPropuestaVista(null)}>
                    <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <div className="modal-header">
                            <h3 className="h3">
                                <Sparkles size={18} className="ai-icon" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                Detalle de Propuesta IA
                            </h3>
                            <button className="btn-icon" onClick={() => setPropuestaVista(null)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body ai-detalle-modal">
                            {propuestaVista.razonamiento && (
                                <div className="ai-detalle-razon">
                                    <div className="ai-detalle-label">Razonamiento</div>
                                    <p>{propuestaVista.razonamiento}</p>
                                </div>
                            )}

                            {propuestaVista.cliente && (propuestaVista.cliente.nombre || propuestaVista.cliente.empresa) && (
                                <div className="ai-detalle-section">
                                    <div className="ai-detalle-label">Cliente</div>
                                    <div className="ai-detalle-cliente">
                                        {propuestaVista.cliente.id ? (
                                            <span className="badge success">Cliente existente</span>
                                        ) : propuestaVista.cliente.es_nuevo ? (
                                            <span className="badge info">Cliente nuevo</span>
                                        ) : null}
                                        <div>
                                            {propuestaVista.cliente.nombre && <div><strong>Nombre:</strong> {propuestaVista.cliente.nombre}</div>}
                                            {propuestaVista.cliente.empresa && <div><strong>Empresa:</strong> {propuestaVista.cliente.empresa}</div>}
                                            {propuestaVista.cliente.email && <div><strong>Email:</strong> {propuestaVista.cliente.email}</div>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {propuestaVista.servicios?.length > 0 && (
                                <div className="ai-detalle-section">
                                    <div className="ai-detalle-label">Servicios ({propuestaVista.servicios.length})</div>
                                    {propuestaVista.servicios.map((s, i) => (
                                        <div key={i} className="ai-detalle-servicio">
                                            <h4 className="ai-detalle-servicio-nombre">{s.tipo_servicio_nombre}</h4>
                                            {s.detalle_html && (
                                                <div
                                                    className="ai-detalle-servicio-html"
                                                    dangerouslySetInnerHTML={{ __html: s.detalle_html }}
                                                />
                                            )}
                                            {s.asignaciones?.length > 0 && (
                                                <div className="ai-detalle-meta-row">
                                                    <strong>Personal:</strong>
                                                    {s.asignaciones.map((a, j) => (
                                                        <span key={j} className="ai-pill">
                                                            {a.horas}h
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            {s.costos_fijos?.length > 0 && (
                                                <div className="ai-detalle-meta-row">
                                                    <strong>Costos fijos:</strong> {s.costos_fijos.length} item(s)
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {propuestaVista.extras?.length > 0 && (
                                <div className="ai-detalle-section">
                                    <div className="ai-detalle-label">Extras</div>
                                    {propuestaVista.extras.map((e, i) => (
                                        <div key={i} className="ai-detalle-extra">
                                            <span>{e.detalle}</span>
                                            <span className="ai-detalle-extra-costo">S/ {parseFloat(e.costo || 0).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn ghost" onClick={() => setPropuestaVista(null)}>Cerrar</button>
                            <button className="btn neon" onClick={() => { setPropuestaActual(propuestaVista); setPropuestaVista(null); aplicar(); }}>
                                Crear cotización <ArrowRight size={14} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GenerarCotizacionIA;
